import { BaseText } from '@/src/components/BaseText'
import { DeleteConfirmModal, EditMenu } from '@/src/components/EditMenu'
import { InformationTooltip } from '@/src/components/InformationTooltip'
import { LocationBadge } from '@/src/components/LocationBadge'
import type { AdjustmentRow } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.types'
import { formatAdjustmentType } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/table/adjustmentTable.utils'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { appColors } from '@/src/theme'
import { formatMeasurement } from '@/src/utils/formatMeasurement'
import { Flex, Skeleton, Td, Tr, useDisclosure } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import LoadPlanningAdjustmentModal from '../LoadPlanningAdjustmentModal'
import LoadPlanningMeasureModal from '../LoadPlanningMeasureModal/LoadPlanningMeasureModal'
import { mapAdjustmentRowToLoadPlanningMeasureModalItem } from '../LoadPlanningMeasureModal/LoadPlanningMeasureModal.types'
import { useDeleteAdjustmentMutation } from '../hooks/AdjustmentMutation.hooks'

interface LoadPlanningAdjustmentTableRowProps {
  row: AdjustmentRow
  isInjecting: boolean
}

export const LoadPlanningAdjustmentTableRow = ({ row, isInjecting }: LoadPlanningAdjustmentTableRowProps) => {
  const { mutateAsync: deleteAdjustment, isPending: isDeleting } = useDeleteAdjustmentMutation()
  const toast = useBaseToast()
  const { t } = useTranslation()
  const adjustmentModal = useDisclosure()
  const deleteConfirmModal = useDisclosure()
  const measureModal = useDisclosure()

  const hasWarnings = (row.warnings?.length ?? 0) > 0
  const isMeasureIntegration = row.type === 'measure'

  const handleDelete = async () => {
    try {
      await deleteAdjustment(row.id)
      toast({
        status: 'success',
        title: t('components.procurement.forecasting.sync_measure_to_adjustment.adjustment_deleted'),
      })
    } catch (error) {
      toast({
        status: 'error',
        title: t('error'),
      })
    }
  }

  return (
    <Tr _hover={{ bg: 'gray.50' }}>
      <Td>
        <BaseText>{row.name}</BaseText>
      </Td>

      <Td>
        <BaseText>{formatAdjustmentType(row.type, t)}</BaseText>
      </Td>

      <Td>
        <BaseText>
          {row.scope === 'COMPANY' || row.locationId === undefined ? (
            <LocationBadge locationName={t('components.procurement.forecasting.adjustment_table.all_locations_badge')} showGroupIcon />
          ) : (
            <LocationBadge locationId={row.locationId} />
          )}
        </BaseText>
      </Td>

      <Td textAlign='right'>
        {isInjecting ? (
          <Skeleton width={'64px'} height='16px' ml='auto' />
        ) : (
          <Flex color={hasWarnings ? appColors.warning : undefined} justifyContent={'end'} alignItems={'center'} gap={3}>
            {hasWarnings && (
              <InformationTooltip
                color={appColors.warning}
                label={t('components.procurement.forecasting.adjustment_table.warning_tooltip')}
              />
            )}
            <BaseText>
              {(row.consumptionDelta ?? 0) > 0 ? '+' : ''}
              {formatMeasurement(row.consumptionDelta, row.unit ?? 'kWh')}
            </BaseText>
          </Flex>
        )}
      </Td>
      <Td>
        <EditMenu
          onEdit={isMeasureIntegration ? measureModal.onOpen : adjustmentModal.onOpen}
          onConfirmModalDelete={handleDelete}
          isLoading={isDeleting}
        />
      </Td>
      {adjustmentModal.isOpen && (
        <LoadPlanningAdjustmentModal
          isOpen={adjustmentModal.isOpen}
          onClose={adjustmentModal.onClose}
          adjustment={row}
          onDelete={deleteConfirmModal.onOpen}
        />
      )}
      {deleteConfirmModal.isOpen && (
        <DeleteConfirmModal isOpen={deleteConfirmModal.isOpen} onClose={deleteConfirmModal.onClose} onAccept={handleDelete} />
      )}
      {measureModal.isOpen && (
        <LoadPlanningMeasureModal
          isOpen={measureModal.isOpen}
          onClose={measureModal.onClose}
          measureItem={mapAdjustmentRowToLoadPlanningMeasureModalItem(row)}
          onDelete={deleteConfirmModal.onOpen}
        />
      )}
    </Tr>
  )
}
